import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useRouter } from "next/router";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSWRConfig } from "swr";
import type { Staff, StaffParam } from "~/models/Staff";
import { buildDefaultStaff, createValidationSchema } from "~/models/Staff";
import { AUTH_NAME } from "~/models/StaffAuthorization";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/Staff";
import { generatePath } from "~/utils/generatePath";

type Props = {
	staff?: Staff | null;
};

export const FormInputPage: React.FC<Props> = ({ staff = null }: Props) => {
	const { notify } = useNotification();
	const { mutate: globalMutate } = useSWRConfig();
	const router = useRouter();
	const methods = useForm<StaffParam>({
		defaultValues: staff ?? buildDefaultStaff(),
		mode: "onBlur",
		resolver: yupResolver(createValidationSchema)
	});
	const {
		formState: { errors }
	} = methods;

	const isEditable = React.useMemo(
		() => staff === null || staff.editable,
		[staff]
	);

	const handleUpdateStatus = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (methods.watch(AUTH_NAME.completeStatus)) {
				methods.setValue(AUTH_NAME.completeStatus, false);
			}
			if (methods.watch(AUTH_NAME.constructionStatus)) {
				methods.setValue(AUTH_NAME.constructionStatus, false);
			}
			methods.setValue(AUTH_NAME.status, e.target.checked, {
				shouldDirty: true
			});
		},
		[methods]
	);

	const handleUpdateAuth = React.useCallback(
		(
			value: (typeof AUTH_NAME)[keyof typeof AUTH_NAME],
			e: React.ChangeEvent<HTMLInputElement>
		) => {
			methods.setValue(value, e.target.checked, { shouldDirty: true });
		},
		[methods]
	);

	return (
		<Box width={360}>
			<FormProvider {...methods}>
				<form
					onSubmit={methods.handleSubmit(async (submitValue: StaffParam) => {
						const response =
							staff === null
								? await create(submitValue)
								: await update(submitValue, staff.id);
						if (isSuccess(response)) {
							if (staff !== null) {
								globalMutate(generatePath("/staffs/:id", { id: staff.id }));
							}
							router.push("/settings/staffs");
							notify({
								content: staff === null ? "追加しました" : "更新しました",
								variant: "success"
							});
							return;
						}
						notify({ content: "エラーが発生しました。", variant: "error" });
					})}
				>
					<FormGroup variant="vertical">
						<FormControl chip="必須" isInvalid={!!errors.name} label="名前">
							<Input
								disabled={!isEditable}
								isInvalid={!!errors.name}
								placeholder="例）内装 太郎"
								{...methods.register("name")}
							/>
							<FormControlText error={errors.name?.message} />
						</FormControl>
						<Divider />
						<FormControl
							chip="必須"
							isInvalid={!!errors.email}
							label="メールアドレス"
						>
							<Input
								disabled={!isEditable}
								isInvalid={!!errors.email}
								placeholder="例）itandi.tarou@example.com"
								{...methods.register("email")}
							/>
							<FormControlText error={errors.email?.message} />
						</FormControl>
						<Divider />
						<FormControl isInvalid={!!errors.phoneNumber} label="電話番号">
							<Input
								disabled={staff !== null && staff.authUserId !== null}
								isInvalid={!!errors.phoneNumber}
								placeholder="例）070-1234-5678"
								{...methods.register("phoneNumber")}
							/>
							<FormControlText error={errors.phoneNumber?.message} />
						</FormControl>
						<Divider />
						<FormControl label="権限">
							<Grid gap={designTokens["spacing/xs"]}>
								<InputCheckBox
									checked={methods.watch(AUTH_NAME.setting)}
									label="設定画面へのアクセス"
									onChange={(e): void => {
										handleUpdateAuth(AUTH_NAME.setting, e);
									}}
								/>
								<InputCheckBox
									checked={methods.watch(AUTH_NAME.status)}
									label="案件のステータスを進める"
									onChange={handleUpdateStatus}
								/>
								<InputCheckBox
									checked={methods.watch(AUTH_NAME.constructionStatus)}
									disabled={!methods.watch(AUTH_NAME.status)}
									label="工事の発注 (「工事完了待ち」に進める)"
									onChange={(e): void => {
										handleUpdateAuth(AUTH_NAME.constructionStatus, e);
									}}
								/>
								<InputCheckBox
									checked={methods.watch(AUTH_NAME.completeStatus)}
									disabled={!methods.watch(AUTH_NAME.status)}
									label="案件の完了 (「完了」に進める)"
									onChange={(e): void => {
										handleUpdateAuth(AUTH_NAME.completeStatus, e);
									}}
								/>
								<InputCheckBox
									checked={methods.watch(AUTH_NAME.cancel)}
									label="案件の取り下げ、削除"
									onChange={(e): void => {
										handleUpdateAuth(AUTH_NAME.cancel, e);
									}}
								/>
							</Grid>
						</FormControl>
					</FormGroup>
					<Button
						disabled={!methods.formState.isDirty}
						loading={methods.formState.isSubmitting}
						marginTop={designTokens["spacing/m"]}
						type="submit"
						variant="primary"
					>
						{staff === null ? "追加" : "更新"}
					</Button>
				</form>
			</FormProvider>
		</Box>
	);
};
